import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import DoctoresService from "../../services/DoctoresService";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import EstudiosService from "../../services/EstudiosService";
import SolicitudesEstudiosService from "../../services/SolicitudesEstudiosService";
import TelefonosService from "../../services/TelefonosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import {
  Select,
  MenuItem,
  Paper,
  Typography,
  Stack,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const DoctoresRemitidos = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [doctoresList, setDoctoresList] = useState([]);
  const [solicitudesList, setSolicitudesList] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [catalogEstudiosMap, setCatalogEstudiosMap] = useState({});
  const [solicitudesEstudiosMap, setSolicitudesEstudiosMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros
  const [busquedaNombre, setBusquedaNombre] = useState("");
  const [comisionFilter, setComisionFilter] = useState("No"); // "Si" (solo con comisión) | "No" (todos)
  const [ordenamiento, setOrdenamiento] = useState("pacientes"); // "pacientes" | "monto" | "fechaNac"
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  useEffect(() => {
    document.title = "Doctores Remitidos";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resDoc, resSol, resSuc, resEst] = await Promise.all([
        DoctoresService.getDoctores().catch(() => ({ data: [] })),
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        EstudiosService.getEstudios().catch(() => ({ data: [] })),
      ]);

      const listaDoc = Array.isArray(resDoc?.data?.doctores)
        ? resDoc.data.doctores
        : Array.isArray(resDoc?.data)
        ? resDoc.data
        : [];

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaEst = Array.isArray(resEst?.data?.estudios)
        ? resEst.data.estudios
        : Array.isArray(resEst?.data)
        ? resEst.data
        : [];

      // Mapear catálogo de estudios con precio/costo
      const catEstMap = {};
      listaEst.forEach((e) => {
        if (e && (e.idEstudio || e.id)) {
          const idKey = e.idEstudio || e.id;
          catEstMap[idKey] = {
            idEstudio: idKey,
            codigo: e.codigo || e.clave || "",
            nombre: e.nombre || e.nombreEstudio || "",
            precio: Number(e.precio || e.costo || e.importe || 0),
          };
        }
      });

      setDoctoresList(listaDoc);
      setSolicitudesList(listaSol);
      setSucursales(listaSuc);
      setCatalogEstudiosMap(catEstMap);

      // Cargar estudios para cada solicitud vía SolicitudesEstudiosService para obtener los precios exactos
      const estPorSolMap = {};
      await Promise.all(
        listaSol.map(async (sol) => {
          if (sol && (sol.idSolicitud || sol.id)) {
            const solId = sol.idSolicitud || sol.id;
            try {
              const resEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(solId);
              const listEst = Array.isArray(resEst)
                ? resEst
                : Array.isArray(resEst?.estudios)
                ? resEst.estudios
                : Array.isArray(resEst?.data)
                ? resEst.data
                : [];
              if (listEst.length > 0) {
                estPorSolMap[solId] = listEst;
              }
            } catch (e) {}
          }
        })
      );

      setSolicitudesEstudiosMap(estPorSolMap);
    } catch (error) {
      console.error("Error al cargar datos para Doctores Remitidos:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al consultar los doctores y solicitudes del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper para verificar si una solicitud está cancelada
  const isSolicitudCancelada = (sol) => {
    const est = String(sol.estatus || sol.estatusPago || sol.estado || "").toUpperCase();
    return (
      est === "C" ||
      est === "CANCELADO" ||
      est === "CANCELADA" ||
      sol.esCancelada === 1 ||
      sol.esCancelada === true
    );
  };

  // Helper para calcular el monto/total de una solicitud
  const obtenerTotalSolicitud = (sol) => {
    // 1. Intentar obtener de propiedades directas de la solicitud
    const directTotal = Number(
      sol.totalPagar ??
      sol.total ??
      sol.costoTotal ??
      sol.totalNeto ??
      sol.importeTotal ??
      sol.monto ??
      sol.precioTotal ??
      0
    );

    if (!isNaN(directTotal) && directTotal > 0) {
      return directTotal;
    }

    // 2. Si las propiedades directas son 0 o undefined, calcular sumando sus estudios
    const solId = sol.idSolicitud || sol.id;
    const estudiosSol = solicitudesEstudiosMap[solId] || sol.estudios || sol.solicitudEstudios || sol.detalles || [];

    if (Array.isArray(estudiosSol) && estudiosSol.length > 0) {
      const sumaEstudios = estudiosSol.reduce((acc, est) => {
        const idEst = est.idEstudio || est.id;
        const catObj = catalogEstudiosMap[idEst] || {};
        const costo = Number(
          est.costo ??
          est.precio ??
          est.importe ??
          est.total ??
          catObj.precio ??
          0
        );
        const desc = Number(est.descuento ?? 0);
        const neto = costo - desc > 0 ? costo - desc : costo;
        return acc + (isNaN(neto) ? 0 : neto);
      }, 0);

      if (sumaEstudios > 0) return sumaEstudios;
    }

    return 0;
  };

  // Helper para verificar si una solicitud corresponde a un doctor específico
  const solicitudEsDeDoctor = (sol, docId, docNomCompleto) => {
    const dIdStr = String(docId);

    // 1. Coincidencia por ID directo
    const directIds = [
      sol.idDoctor,
      sol.idDoctor1,
      sol.idDoctor2,
      sol.idDoctor3,
      sol.id_doctor,
      sol.doctorId,
      sol.idDoctorRemitido,
    ];
    if (directIds.some((id) => id && String(id) === dIdStr)) return true;

    // 2. Coincidencia en arreglos idDoctores o idDoctoresN
    const idsArr = Array.isArray(sol.idDoctores)
      ? sol.idDoctores
      : Array.isArray(sol.idDoctoresN)
      ? sol.idDoctoresN
      : [];
    if (idsArr.some((id) => id && String(id) === dIdStr)) return true;

    // 3. Coincidencia en arreglo de objetos doctores
    if (Array.isArray(sol.doctores)) {
      if (sol.doctores.some((d) => d && String(d.idDoctor || d.id) === dIdStr)) return true;
    }

    // 4. Coincidencia por nombre completo si no hay IDs
    if (docNomCompleto && docNomCompleto.trim().length > 3) {
      const docNomClean = docNomCompleto.toLowerCase().trim();
      const solDocNames = [sol.nombreDoctor, sol.doctor, sol.nombreDoctor1, sol.nombreDoctor2, sol.doctorNombre]
        .filter(Boolean)
        .map((n) => String(n).toLowerCase().trim());

      if (solDocNames.some((n) => n.includes(docNomClean) || docNomClean.includes(n))) return true;
    }

    return false;
  };

  // Cálculo de estadísticas y filtrado de doctores
  const doctoresCalculados = useMemo(() => {
    // 1. Filtrar solicitudes dentro del rango de fechas
    const solicitudesRango = solicitudesList.filter((sol) => {
      const rawFecha = sol.fecha || sol.createdAt || sol.fechaCreacion || sol.fechaSolicitud || "";
      const fechaSolStr = String(rawFecha).substring(0, 10);
      if (fechaDe && fechaSolStr < fechaDe) return false;
      if (fechaA && fechaSolStr > fechaA) return false;
      return true;
    });

    // 2. Procesar cada doctor
    const result = doctoresList.map((doc) => {
      const docId = doc.idDoctor || doc.id;
      const nom = [doc.nombre, doc.apPaterno, doc.apMaterno].filter(Boolean).join(" ").trim();
      const docNomCompleto = nom || doc.nombreDoctor || `Doctor #${docId}`;

      const institucion = doc.institucion || doc.nombreInstitucion || doc.hospital || doc.clinica || "-";
      const fechaCumpleanos = doc.fechaNacimiento || doc.fechaCumpleanos || doc.cumpleanos || doc.fechaNac || "-";

      const email = doc.email || doc.correo || doc.correoElectronico || "-";
      const telefono = doc.telefono || doc.tel || doc.celular || doc.telefonoContacto || "-";

      const dirParts = [doc.calle, doc.colonia, doc.ciudad, doc.estado].filter(Boolean).join(", ");
      const direccion = dirParts || doc.direccion || doc.domicilio || "-";

      // Determinar si tiene comisión
      const esComision = Boolean(
        doc.esComision === 1 ||
        doc.esComision === true ||
        doc.esComision === "1" ||
        doc.conComision === true ||
        doc.conComision === 1 ||
        doc.comision === true ||
        doc.comision === 1 ||
        Number(doc.porcentajeComision || doc.comisionPorcentaje || 0) > 0
      );

      // Calcular solicitudes remitidas en el rango de fechas
      const solRemitidas = solicitudesRango.filter((sol) =>
        solicitudEsDeDoctor(sol, docId, docNomCompleto)
      );

      const pacientesRemitidos = solRemitidas.length;

      // Calcular monto remitido (excluyendo canceladas)
      const montoRemitido = solRemitidas.reduce((acc, sol) => {
        if (isSolicitudCancelada(sol)) return acc;
        const totalSol = obtenerTotalSolicitud(sol);
        return acc + totalSol;
      }, 0);

      return {
        idDoctor: docId,
        institucion,
        nombreCompleto: docNomCompleto,
        fechaCumpleanos,
        pacientesRemitidos,
        montoRemitido,
        direccion,
        email,
        telefono,
        esComision,
      };
    });

    // 3. Aplicar Filtro de Comisión
    let filtrados = result.filter((doc) => {
      if (comisionFilter === "Si") {
        if (!doc.esComision) return false;
      }
      return true;
    });

    // 4. Aplicar Búsqueda por Nombre del Doctor
    if (busquedaNombre.trim() !== "") {
      const q = busquedaNombre.trim().toLowerCase();
      filtrados = filtrados.filter((doc) =>
        doc.nombreCompleto.toLowerCase().includes(q)
      );
    }

    // 5. Aplicar Ordenamiento
    if (ordenamiento === "pacientes") {
      filtrados.sort((a, b) => b.pacientesRemitidos - a.pacientesRemitidos);
    } else if (ordenamiento === "monto") {
      filtrados.sort((a, b) => b.montoRemitido - a.montoRemitido);
    } else if (ordenamiento === "fechaNac") {
      filtrados.sort((a, b) => {
        const fA = a.fechaCumpleanos === "-" ? "" : String(a.fechaCumpleanos);
        const fB = b.fechaCumpleanos === "-" ? "" : String(b.fechaCumpleanos);
        if (!fA && !fB) return 0;
        if (!fA) return 1;
        if (!fB) return -1;
        return fB.localeCompare(fA);
      });
    }

    return filtrados;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    doctoresList,
    solicitudesList,
    busquedaNombre,
    comisionFilter,
    ordenamiento,
    fechaDe,
    fechaA,
    catalogEstudiosMap,
    solicitudesEstudiosMap,
  ]);

  // Exportar Doctores Remitidos a PDF Institucional
  const handleExportarPDF = async () => {
    if (doctoresCalculados.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No existen doctores que coincidan con los filtros seleccionados.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF();
        const startY = 12;

    let sucursalActualObj = sucursales[0] || {};
    let sucursalTelefonosStr = "";
    if (sucursalActualObj.idSucursal) {
      try {
        const resTels = await TelefonosService.getTelefonosPorSucursal(sucursalActualObj.idSucursal);
        const listaTels = Array.isArray(resTels) ? resTels : resTels?.telefonos || [];
        sucursalTelefonosStr = listaTels.map((t) => t.telefono || t).join(", ");
      } catch (e) {}
    }

    const sucursalNomHeader = (sucursalActualObj.nombre || "LABORATORIO").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;
    const telText = `Tel. ${sucursalTelefonosStr || sucursalActualObj.telefono || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 28);
    } catch (e) {}

    // Rectángulo institucional "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Datos sucursal a la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomHeader, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });
    currY += 4;
    doc.text(telText, 196, currY, { align: "right" });

    // Título del reporte
    let ordText = "Pacientes Remitidos";
    if (ordenamiento === "monto") ordText = "Monto Remitido";
    if (ordenamiento === "fechaNac") ordText = "Fecha de Nacimiento";

    currY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 35, 79);
    doc.text("DOCTORES REMITIDOS", 14, currY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Rango de Fechas: De ${fechaDe} A ${fechaA} | Orden: ${ordText} | Con Comisión: ${comisionFilter}`, 14, currY + 4);

    let nextY = currY + 10;
    const bodyRowsPdf = doctoresCalculados.map((d, idx) => [
      String(idx + 1),
      String(d.idDoctor),
      d.institucion,
      d.nombreCompleto,
      d.fechaCumpleanos,
      String(d.pacientesRemitidos),
      `$${d.montoRemitido.toFixed(2)}`,
      d.direccion,
      `Email: ${d.email}\nTel: ${d.telefono}`,
    ]);

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          "#",
          "Clave",
          "Institución",
          "Nombre",
          "Cumpleaños",
          "Pacientes Remitidos",
          "Monto Remitido",
          "Dirección",
          "Contacto",
        ],
      ],
      body: bodyRowsPdf,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 },
      styles: { fontSize: 6.5, cellPadding: 1.5, valign: "middle" },
      columnStyles: {
        0: { width: 8, halign: "center" },
        1: { width: 12, halign: "center" },
        2: { width: 22 },
        3: { width: 35 },
        4: { width: 20, halign: "center" },
        5: { width: 18, halign: "center" },
        6: { width: 22, halign: "right" },
        7: { width: 30 },
        8: { width: 25 },
      },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Doctores_Remitidos_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Doctores Remitidos en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  DOCTORES REMITIDOS
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros de Búsqueda
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Nombre del doctor */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "30%", backgroundColor: "#f8f9fa" }}>
                        Nombre del doctor:
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Escriba para buscar doctor por nombre..."
                          value={busquedaNombre}
                          onChange={(e) => setBusquedaNombre(e.target.value)}
                          sx={{ width: "380px" }}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Fila Doctores con comisión */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Doctores con comisión:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={comisionFilter}
                          onChange={(e) => setComisionFilter(e.target.value)}
                          sx={{ width: "180px" }}
                        >
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="Si">Si</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Adicionales (Radio buttons de ordenamiento) */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Adicionales:
                      </TableCell>
                      <TableCell>
                        <RadioGroup
                          row
                          value={ordenamiento}
                          onChange={(e) => setOrdenamiento(e.target.value)}
                        >
                          <FormControlLabel
                            value="pacientes"
                            control={<Radio size="small" sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }} />}
                            label={<Typography variant="body2">Ordenar por Total Pacientes Remitidos</Typography>}
                          />
                          <FormControlLabel
                            value="monto"
                            control={<Radio size="small" sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }} />}
                            label={<Typography variant="body2">Ordenar por Total Monto Remitido</Typography>}
                          />
                          <FormControlLabel
                            value="fechaNac"
                            control={<Radio size="small" sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }} />}
                            label={<Typography variant="body2">Ordenar por Fecha de Nacimiento</Typography>}
                          />
                        </RadioGroup>
                      </TableCell>
                    </TableRow>

                    {/* Fila Selecciona un rango de fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Selecciona un rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item>
                            <Typography variant="body2" component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                              De:
                            </Typography>
                            <TextField
                              type="date"
                              size="small"
                              value={fechaDe}
                              onChange={(e) => setFechaDe(e.target.value)}
                              sx={{ width: "170px" }}
                            />
                          </Grid>
                          <Grid item>
                            <Typography variant="body2" component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                              A:
                            </Typography>
                            <TextField
                              type="date"
                              size="small"
                              value={fechaA}
                              onChange={(e) => setFechaA(e.target.value)}
                              sx={{ width: "170px" }}
                            />
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Contenedor Principal de la Tabla de Doctores Remitidos (Sin Paginación) */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                </Box>
              ) : doctoresCalculados.length === 0 ? (
                <Box
                  sx={{
                    width: "95%",
                    maxWidth: "1200px",
                    textAlign: "center",
                    py: 4,
                    mb: 3,
                    backgroundColor: "#fff8e1",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: "#b78103", fontWeight: "bold" }}>
                    No existen doctores remitidos para los filtros seleccionados.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                  {/* Resumen Total */}
                  <Box sx={{ mb: 2, textAlign: "right" }}>
                    <Typography variant="body2" sx={{ color: "#006787", fontWeight: "bold" }}>
                      Total de doctores registrados: {doctoresCalculados.length}
                    </Typography>
                  </Box>

                  {/* Tabla Principal */}
                  <Table
                    sx={{
                      width: "100%",
                      backgroundColor: "white",
                      border: "1px solid #c4d1db",
                      borderRadius: "8px",
                      overflow: "hidden",
                      mb: 3,
                      "& .MuiTableCell-root": { padding: "8px 10px", fontSize: "0.82rem" },
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#006787" }}>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "4%", textAlign: "center" }}>
                          #
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "7%", textAlign: "center" }}>
                          Clave
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "14%" }}>
                          Institución
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "20%" }}>
                          Nombre
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "12%", textAlign: "center" }}>
                          Fecha de cumpleaños
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "12%", textAlign: "center" }}>
                          Pacientes remitidos
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "12%", textAlign: "right" }}>
                          Monto remitido
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "14%" }}>
                          Dirección
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", backgroundColor: "#006787", width: "15%" }}>
                          Contacto
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {doctoresCalculados.map((doc, idx) => (
                        <TableRow key={doc.idDoctor || idx} sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
                          {/* 1. # */}
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {idx + 1}
                          </TableCell>

                          {/* 2. Clave */}
                          <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>
                            {doc.idDoctor}
                          </TableCell>

                          {/* 3. Institución */}
                          <TableCell>{doc.institucion}</TableCell>

                          {/* 4. Nombre */}
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {doc.nombreCompleto}
                          </TableCell>

                          {/* 5. Fecha de cumpleaños */}
                          <TableCell align="center">
                            {doc.fechaCumpleanos}
                          </TableCell>

                          {/* 6. Pacientes remitidos */}
                          <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787" }}>
                            {doc.pacientesRemitidos}
                          </TableCell>

                          {/* 7. Monto remitido */}
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                            ${doc.montoRemitido.toFixed(2)}
                          </TableCell>

                          {/* 8. Dirección */}
                          <TableCell>{doc.direccion}</TableCell>

                          {/* 9. Contacto */}
                          <TableCell>
                            <Typography variant="caption" sx={{ display: "block", color: "#333" }}>
                              <strong>Email:</strong> {doc.email}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block", color: "#333", mt: 0.2 }}>
                              <strong>Teléfono:</strong> {doc.telefono}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón Flotante Lateral Derecho: PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar Doctores Remitidos a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default DoctoresRemitidos;
